/**
 * Inbox's side of `@qw/auth`'s `onAuthFailure`.
 *
 * The report matters most here. A failed `Inbox` fetch leaves `userInbox` undefined, which
 * `inbox-filter-provider` turns into `inboxIds: []` and `use-folder-counts` turns into four skipped
 * live subscriptions — so the user sees an empty inbox rather than a broken one, and nothing on the
 * path throws, which means PostHog's exception autocapture cannot see it either (and inbox does not
 * pass `allowExceptionCapture` at all). An explicit emit is the only signal available.
 *
 * Mirrors `web/src/brokerage/auth/auth-error-telemetry.ts`, including the swallow — see
 * `captureUserFacingError`.
 */

import type { AuthFailureReport } from "@qw/auth";
import {
  ErrorSeverity,
  UserFacingErrorKind,
  UserFacingErrorReason,
  UserFacingErrorSurface,
} from "@third-plane/posthog-integration-interface";

import { captureUserFacingError } from "./posthog";

/**
 * `signed-out` is `info`, not `error`: an expired credential is the system working, and pairing it
 * with `error` severity would bury the two readings that mean something is broken.
 */
const SEVERITY_BY_KIND = {
  "signed-out": ErrorSeverity.Info,
  other: ErrorSeverity.Error,
  unreachable: ErrorSeverity.Error,
} as const;

const KIND_BY_FAILURE = {
  "signed-out": UserFacingErrorKind.Authorization,
  other: UserFacingErrorKind.Unknown,
  unreachable: UserFacingErrorKind.Network,
} as const;

/**
 * Category telemetry only — the operation name, the route, and which of the three readings it was.
 * Never the error message: an auth failure's message can carry an email or a token fragment.
 */
export function captureAuthFailure({ kind, operation }: AuthFailureReport): void {
  captureUserFacingError("[auth telemetry] user_facing_error emit failed", {
    routeName: typeof window === "undefined" ? "" : window.location.pathname,
    surface: UserFacingErrorSurface.Auth,
    errorKind: KIND_BY_FAILURE[kind],
    severity: SEVERITY_BY_KIND[kind],
    // `fetch_failed` is only true of `unreachable` — the other two did get an answer. A blurred
    // category costs more than no category, so the rest leave it unset.
    ...(kind === "unreachable" ? { reason: UserFacingErrorReason.FetchFailed } : {}),
    component: "auth-account-provider",
    operation,
  });
}
